/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { DebounceInput } from "react-debounce-input";

import { LayoutContext } from "../../components/layout";
import { addTimeToDateString } from "../../utils/inject";

const AllReportsFilter = ({ onFilterChange, filter }) => {
  const { user, readPhysicians } = useContext(LayoutContext);
  const [physicians, setPhysicians] = useState([]);

  useEffect(() => {
    if (readPhysicians) setPhysicians(readPhysicians);
  }, [readPhysicians]);

  const physiciansFullName = (data) => {
    const selectedPhysician = physicians.find((item) => item.id === parseInt(data, 10));
    if (selectedPhysician) {
      return `${selectedPhysician.family}, ${selectedPhysician.name} (${selectedPhysician.username})`;
    }
  };

  const physiciansSelection = (list, field) => {
    return (
      <>
        <option value="">Search by {field}...</option>
        {list.map((item) => (
          <option key={item.id} value={item.id}>{`${item.family}, ${item.name} (${item.username})`}</option>
        ))}
      </>
    );
  };

  const physiciansDisplayInfo = (selection, type, field) => {
    const physiciansList = physicians
      .filter((i) => i.rtDefibrillators || i.rtPaceMakers)
      .sort((a, b) => {
        if (a.family > b.family) return 1;
        if (b.family > a.family) return -1;
        return 0;
      });
    const physiciansListFiltered = physiciansList.filter((elem) => elem.id != selection);
    return (
      <>
        {type === "filtered" ? (
          <>
            <option value={selection}>{physiciansFullName(selection)}</option>
            {physiciansSelection(physiciansListFiltered, field)}
          </>
        ) : (
          physiciansSelection(physiciansList, field)
        )}
      </>
    );
  };

  const onChange = (e) => {
    const elem = e.target;
    const dataSet = elem.dataset;
    const tfilter = { ...filter };
    if (elem.value) tfilter[dataSet.key] = { ...dataSet, value: elem.value };
    else if (tfilter[dataSet.key]) delete tfilter[dataSet.key];
    onFilterChange({ ...tfilter });
  };
  return (
    <form className="search toggleSearch" id="searchContent">
      {/* <small>
    <pre>{JSON.stringify(filter, null, 2)}</pre>
   </small> */}
      <div className="advOpt">
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Last Name</span>
              <DebounceInput
                id="last-name-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Last Name..."
                value={(filter && filter["patient.family"]?.value) || ""}
                onChange={onChange}
                data-key="patient.family"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>First Name</span>
              <DebounceInput
                id="first-name-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by First Name..."
                value={(filter && filter["patient.name"]?.value) || ""}
                onChange={onChange}
                data-key="patient.name"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Date of Birth</span>
            </label>
            <DatePicker
              id="date-of-birth-filter"
              placeholder="Search by Date of Birth..."
              selected={
                (filter &&
                  filter["patient.dob"]?.value &&
                  new Date(addTimeToDateString(filter["patient.dob"]?.value))) ||
                ""
              }
              onChange={(date) => {
                const tfilter = { ...filter };
                tfilter["patient.dob"] = { com: "$eq", key: "patient.dob", value: date.toStandardDate() };
                onFilterChange({ ...tfilter });
              }}
            />
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Location</span>
              <select id="location-filter" onChange={onChange} data-key="location.name" data-com="$eq">
                <option selected="selected" value="">
                  Search by Location...
                </option>
                {user?.locations?.map((location) => (
                  <option key={location.name} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Medical Record Number</span>
              <DebounceInput
                id="medical-record-number-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Medical Record Number..."
                onChange={onChange}
                data-key="mrn"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Model</span>
              <DebounceInput
                id="device-model-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Device Model..."
                onChange={onChange}
                data-key="device.modelName"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Serial Number</span>
              <DebounceInput
                id="device-serial-number-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Device Serial Number..."
                onChange={onChange}
                data-key="device.serialNumber"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Type</span>
              <select id="device-type-filter" onChange={onChange} data-key="device.deviceType" data-com="$lk">
                <option selected="selected" value="">
                  Search by Device Type...
                </option>
                {[
                  "PACEMAKER",
                  "CRTP",
                  "ICD",
                  "CRTD",
                  "ILR",
                  "IPG",
                  "PPM",
                  "MONITOR",
                  "ICM",
                  "MCT",
                  "EVENT",
                  "HOLTER",
                  "BGM",
                  "BP_CUFF",
                  "WEIGHTSCALE",
                  "OTHER",
                ].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Device Manufacturer</span>
              <select
                id="device-manufacturer-filter"
                onChange={onChange}
                data-key="device.manufacturerName"
                data-com="$eq"
              >
                <option selected="selected" value="">
                  Search by Device Manufacturer...
                </option>
                {["BIOTRONIK", "BOSTONSCI", "MEDTRONIC", "STJUDE", "BIOTEL", "OTHER"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Status</span>
              <select id="status-filter" onChange={onChange} data-key="interrogationStatus" data-com="$lk">
                <option selected="selected" value="">
                  Search by Status...
                </option>
                {["NOTASSIGNED", "PRELIMINARY", "FINAL", "TERMINATED"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Visit Number</span>
              <DebounceInput
                id="visit-number-filter"
                debounceTimeout={800}
                type="text"
                placeholder="This is a placeholder"
                onChange={onChange}
                data-key="visitNumber"
                data-com="$lk"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>MFR Alert</span>
              <select id="mfr-alert-filter" onChange={onChange} data-key="manufacturerAlert" data-com="$lk">
                <option selected="selected" value="">
                  Search by MFR Alert...
                </option>
                <option value="YES">YES</option>
                <option value="NO">NO</option>
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25 ">
            <label className="inCal">
              <span>Report ID</span>
              <DebounceInput
                id="report-id-filter"
                debounceTimeout={800}
                type="text"
                placeholder="Search by Report ID..."
                onChange={onChange}
                data-key="id"
                data-com="$eq"
              />
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>From (Billable)</span>
              <input id="from-billable-filter" type="date" />
              <i className="ti ti-calendar"></i>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>To (Billable)</span>
              <input id="to-billable-filter" type="date" />
              <i className="ti ti-calendar"></i>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Assigned To</span>
              <select
                id="assigned-to-filter"
                onChange={onChange}
                data-key="readingPhysician.id"
                data-com="$eq"
              >
                {filter && filter["readingPhysician.id"]?.value
                  ? physiciansDisplayInfo(filter["readingPhysician.id"]?.value, "filtered", "Assigned To")
                  : physiciansDisplayInfo("All", "Not filtered", "Assigned To")}
              </select>
            </label>
          </div>
        </div>
        <div className="row rowStretch">
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>30 Day Status</span>
              <select
                id="30-day-status-filter"
                onChange={onChange}
                data-key="thirtyDayBillStatus"
                data-com="$eq"
              >
                <option selected="selected" value="">
                  Search by 30 Day Status...
                </option>
                {["BILLABLE", "INTERIM", "NOTENABLED"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>90 Day Status</span>
              <select
                id="90-day-status-filter"
                onChange={onChange}
                data-key="nintyDayBillStatus"
                data-com="$eq"
              >
                <option selected="selected" value="">
                  Search by 90 Day Status...
                </option>
                {["BILLABLE", "INTERIM", "NOTENABLED"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25">
            <label className="inCal">
              <span>Type</span>
              <select id="type-filter" onChange={onChange} data-key="interrogationType" data-com="$eq">
                <option selected="selected" value="">
                  Search by Type...
                </option>
                {["REMOTE", "POC"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-m-50 col25 colFiller"></div>
        </div>
      </div>
    </form>
  );
};

export default AllReportsFilter;
